import { useEffect, useRef, useState } from "react";

/**
 * useApiData — generic data-fetching hook.
 *
 * @param {() => Promise<any>} fetcher  A zero-argument function that returns a promise.
 * @param {any[]}              deps     Dependency array (same semantics as useEffect).
 * @param {{ enabled?: boolean }} opts  Pass `enabled: false` to skip the fetch.
 *
 * Returns { data, loading, error, errorCode, errorBody } where:
 *   data      — resolved value (null until first successful fetch)
 *   loading   — true while a fetch is in flight
 *   error     — string message if the most recent fetch rejected, else null
 *   errorCode — that rejection's machine-readable code (lib/apiError.js), or null
 *   errorBody — that rejection's parsed response body, or null
 *
 * The hook is cancellation-safe: a stale fetch result is discarded when
 * deps change or the component unmounts before the promise resolves.
 */
export function useApiData(fetcher, deps, { enabled = true } = {}) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(!!enabled);
  const [error, setError]     = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  const [errorBody, setErrorBody] = useState(null);

  // Keep a stable ref to the fetcher so callers can pass inline arrow
  // functions without triggering extra effects.
  const fetcherRef = useRef(fetcher);
  useEffect(() => { fetcherRef.current = fetcher; });

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    let alive = true;
    setLoading(true);
    setError(null);
    setErrorCode(null);
    setErrorBody(null);

    fetcherRef.current()
      .then((result) => {
        if (!alive) return;
        setData(result);
        setLoading(false);
      })
      .catch((err) => {
        if (!alive) return;
        setError(err?.message ?? String(err));
        // Additive (#2453): the message alone cannot discriminate two 403s that
        // differ only by which feature flag refused. Keep the machine-readable
        // code and the raw body beside it so callers can branch on the code —
        // `error` itself stays the plain string every existing caller renders.
        setErrorCode(err?.code ?? null);
        setErrorBody(err?.body ?? null);
        setLoading(false);
      });

    return () => { alive = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...deps]);

  return { data, loading, error, errorCode, errorBody };
}

/**
 * useMutationError — transient error state for user-initiated mutations
 * (#1668). Follows Score.jsx's actionError convention: set a message when a
 * mutation rejects, auto-dismiss it after 4 seconds. Render the message with
 * <ErrorMessage message={actionError} />.
 *
 * Returns [actionError, setActionError].
 */
export function useMutationError() {
  const [actionError, setActionError] = useState(null);

  useEffect(() => {
    if (!actionError) return undefined;
    const t = setTimeout(() => setActionError(null), 4000);
    return () => clearTimeout(t);
  }, [actionError]);

  return [actionError, setActionError];
}
